import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import client from '../../../lib/feathers';

export default function PassportsInfoPanel() {
  const listing = useSelector((s) => s.listings.selected);
  const [listingPassports, setListingPassports] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadListingPassports = async () => {
      if (!listing?.id) return;

      setIsLoading(true);
      setError(null);

      try {
        const service = client.service('admin/listing-passport');
        const result = await service.find({
          query: {
            listingId: listing.id,
            $sort: { createdAt: -1 },
          },
        });
        setListingPassports(result.data || []);
      } catch (e) {
        console.error('Failed to load listing passports:', e);
        setError(e.message);
      } finally {
        setIsLoading(false);
      }
    };

    loadListingPassports();
  }, [listing?.id]);

  const getStatusBadge = (status) => {
    const statusStyles = {
      draft: 'bg-gray-100 text-gray-800',
      active: 'bg-green-100 text-green-800',
      ended: 'bg-yellow-100 text-yellow-800',
      archived: 'bg-red-100 text-red-800',
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusStyles[status] || statusStyles.draft}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (isLoading) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/4 mb-4" />
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded" />
            <div className="h-4 bg-gray-200 rounded w-5/6" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <div className="text-red-600">
          Failed to load passports:
          {' '}
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Passports
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              Wine passports that include this listing
            </p>
          </div>
          <Link
            to="/admin/passports"
            className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-primary bg-primary-light hover:bg-primary-lighter focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
          >
            Manage All Passports
          </Link>
        </div>
      </div>

      {listingPassports.length === 0 ? (
        <div className="px-4 py-12 text-center">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
            />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No passports</h3>
          <p className="mt-1 text-sm text-gray-500">
            This listing is not part of any wine passports yet.
          </p>
          <div className="mt-6">
            <Link
              to="/admin/passports"
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
            >
              View All Passports
            </Link>
          </div>
        </div>
      ) : (
        <ul className="divide-y divide-gray-200">
          {listingPassports.map((lp) => (
            <li key={lp.id} className="px-4 py-4 sm:px-6 hover:bg-gray-50">
              <Link to={`/admin/passports/${lp.passportId}`} className="block">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3">
                      <p className="text-sm font-medium text-primary truncate">
                        {lp.passport?.title || 'Untitled Passport'}
                      </p>
                      {getStatusBadge(lp.passport?.status || 'draft')}
                      {lp.accepted && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          Accepted
                        </span>
                      )}
                    </div>
                    <div className="mt-1 flex items-center gap-4 text-sm text-gray-500">
                      <span>
                        {formatDate(lp.passport?.startDate)}
                        {' - '}
                        {formatDate(lp.passport?.endDate)}
                      </span>
                      {lp.order != null && (
                        <span>
                          Stop #
                          {lp.order + 1}
                        </span>
                      )}
                    </div>
                    {lp.subtitle && (
                      <p className="mt-1 text-sm text-gray-600 italic">
                        &ldquo;
                        {lp.subtitle}
                        &rdquo;
                      </p>
                    )}
                  </div>
                  <div className="ml-4 flex-shrink-0">
                    <svg
                      className="h-5 w-5 text-gray-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </div>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
